import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth/supabaseAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { COURSES } from '@/data/courses';
import { SQL_COURSES } from '@/data/sql';
import { PYTHON_COURSES } from '@/data/pythonCourseData';
import { AI_COURSES } from '@/data/ai';
import { JAVA_COURSES } from '@/data/java';
import { DOTNET_COURSES } from '@/data/dotnet';
import { AZURE_COURSES } from '@/data/azure';
import { checkAndPerformAutoAssignment } from '@/services/autoEnrollmentService';

export interface AssignedCourse {
  id: string;
  course_id: string;
  title: string;
  description: string;
  level: string;
  assigned_date: string;
  last_activity_date: string | null;
  status: string;
  course_type: 'react' | 'sql' | 'python' | 'ai' | 'java' | 'dotnet' | 'azure';
  image?: string;
  totalDuration?: string;
}

// Enhanced helper functions for course data mapping
const getCourseLevel = (course: any): string => {
  return course.level || course.difficulty || 'basic';
};

const getCourseDuration = (course: any): string => {
  return course.totalDuration || course.estimatedTime || course.duration || '2 hours';
};

const getCourseImage = (course: any): string | undefined => {
  return course.image || course.imageSrc;
};

// Enhanced course type detection - UPDATED to include Azure
const detectCourseType = (courseId: string): 'react' | 'sql' | 'python' | 'ai' | 'java' | 'dotnet' | 'azure' => {
  if (courseId.includes('sql') || courseId.startsWith('sql-')) return 'sql';
  if (courseId.includes('python') || courseId.startsWith('python-')) return 'python';
  if (courseId.includes('ai') || courseId.startsWith('ai-')) return 'ai';
  if (courseId.includes('java') || courseId.startsWith('java-')) return 'java';
  if (courseId.includes('dotnet') || courseId.startsWith('dotnet-')) return 'dotnet';
  if (courseId.includes('azure') || courseId.startsWith('azure-')) return 'azure';
  return 'react';
};

export const useUserCourseAssignments = () => {
  const { user, hasAccess, userRole } = useSupabaseAuth();
  const [assignments, setAssignments] = useState<AssignedCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getAllCourses = () => {
    return [
      ...COURSES.map(course => ({ ...course, course_type: 'react' as const })),
      ...SQL_COURSES.map(course => ({ 
        ...course, 
        course_type: 'sql' as const,
        level: getCourseLevel(course),
        totalDuration: getCourseDuration(course)
      })),
      ...PYTHON_COURSES.map(course => ({ 
        ...course, 
        course_type: 'python' as const,
        level: getCourseLevel(course)
      })),
      ...AI_COURSES.map(course => ({ 
        ...course, 
        course_type: 'ai' as const,
        level: getCourseLevel(course)
      })),
      ...JAVA_COURSES.map(course => ({ 
        ...course, 
        course_type: 'java' as const,
        level: getCourseLevel(course),
        totalDuration: getCourseDuration(course)
      })),
      ...DOTNET_COURSES.map(course => ({ 
        ...course, 
        course_type: 'dotnet' as const,
        level: getCourseLevel(course),
        totalDuration: getCourseDuration(course)
      })),
      ...AZURE_COURSES.map(course => ({ 
        ...course, 
        course_type: 'azure' as const,
        level: getCourseLevel(course),
        totalDuration: getCourseDuration(course)
      }))
    ];
  };

  const mapAssignmentToCourse = (assignment: any): AssignedCourse => {
    const allCourses = getAllCourses();
    const courseData = allCourses.find(course => course.id === assignment.course_id);
    
    // If course not found in local data, create a fallback with detected type
    if (!courseData) {
      const detectedType = detectCourseType(assignment.course_id);
      console.warn(`⚠️ Course not found: ${assignment.course_id}`);
      console.log('Available courses:', allCourses.map(c => c.id));
      
      // Enhanced fallback - always create course object
      return {
        id: assignment.id,
        course_id: assignment.course_id,
        title: assignment.course_id.replace(/-/g, ' ').toUpperCase(),
        description: 'Course details loading...',
        level: 'basic',
        assigned_date: assignment.assigned_date || assignment.created_at,
        last_activity_date: assignment.last_activity_date || null,
        status: assignment.status || 'active',
        course_type: detectedType
      };
    }
    
    return {
      id: assignment.id,
      course_id: assignment.course_id,
      title: courseData.title,
      description: courseData.description,
      level: getCourseLevel(courseData),
      assigned_date: assignment.assigned_date || assignment.created_at,
      last_activity_date: assignment.last_activity_date || null,
      status: assignment.status,
      course_type: courseData.course_type,
      image: getCourseImage(courseData),
      totalDuration: getCourseDuration(courseData)
    };
  };

  const fetchAssignments = async () => {
    if (!user) {
      setAssignments([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('=== ENHANCED COURSE ASSIGNMENTS FETCH ===');
      console.log('Fetching course assignments for user:', user.id, 'with role:', userRole);

      // Use safe RLS query that should work with the new policies
      // FIX: Include ALL assigned courses regardless of progress status (active, in_progress, completed)
      // Filter by assignment_status instead of progress status to prevent completed courses from disappearing
      const { data: userAssignments, error: fetchError } = await supabase
        .from('course_assignments')
        .select('*')
        .eq('user_id', user.id)
        .eq('assignment_status', 'assigned')
        .order('last_activity_date', { ascending: false, nullsFirst: false });

      if (fetchError) {
        console.error('Error fetching course assignments:', fetchError);
        throw new Error(`Database error: ${fetchError.message}`);
      }

      console.log('Successfully fetched assignments from database:', userAssignments?.length || 0);
      console.log('Raw assignments data:', userAssignments);

      const assignedCourses: AssignedCourse[] = (userAssignments || []).map(mapAssignmentToCourse);

      console.log('=== ENHANCED COURSE ASSIGNMENTS PROCESSED ===');
      console.log('Processed assigned courses:', assignedCourses.length);
      console.log('Assignment details:', assignedCourses);
      
      setAssignments(assignedCourses);
    } catch (err: any) {
      console.error('Error in fetchAssignments:', err);
      const errorMessage = err.message || 'Failed to load course assignments';
      setError(errorMessage);
      
      // Only show toast for non-recursion errors to avoid spam
      if (!errorMessage.includes('infinite recursion')) {
        toast.error('Failed to load course assignments. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Real-time subscription effect
  useEffect(() => {
    if (!user) return;

    console.log('Setting up real-time subscription for course assignments');
    
    const channel = supabase
      .channel('course_assignments_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'course_assignments',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Real-time course assignment change:', payload);
          
          if (payload.eventType === 'INSERT') {
            const newAssignment = mapAssignmentToCourse(payload.new);
            setAssignments(prev => [newAssignment, ...prev]);
            toast.success(`New course assigned: ${newAssignment.title}`);
          } else if (payload.eventType === 'UPDATE') {
            const updatedAssignment = mapAssignmentToCourse(payload.new);
            setAssignments(prev => 
              prev.map(assignment => 
                assignment.id === updatedAssignment.id ? updatedAssignment : assignment
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setAssignments(prev => 
              prev.filter(assignment => assignment.id !== payload.old.id)
            );
            toast.info('Course assignment removed');
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Initial fetch effect with auto-assignment
  useEffect(() => {
    if (user && userRole !== null) {
      console.log('User and role loaded, checking auto-assignment and fetching assignments');
      
      // Check if staff/admin needs auto-assignment
      if (['admin', 'staff'].includes(userRole)) {
        checkAndPerformAutoAssignment(user.id, userRole).then(() => {
          // Fetch assignments after potential auto-assignment
          fetchAssignments();
        });
      } else {
        fetchAssignments();
      }
    } else if (!user) {
      console.log('No user, clearing assignments');
      setAssignments([]);
      setLoading(false);
      setError(null);
    }
  }, [user, userRole]);

  const getAssignedCoursesByType = (courseType: string) => {
    return assignments.filter(assignment => assignment.course_type === courseType);
  };

  const isUserAssignedToCourse = (courseId: string) => {
    const isAssigned = assignments.some(assignment => assignment.course_id === courseId);
    console.log('Course access check for', courseId, ':', isAssigned, 'Assignments:', assignments.length);
    
    // Admins and Staff can still access courses for browsing/management purposes
    if (hasAccess('admin') || hasAccess('staff')) {
      console.log('Admin/Staff user can access course for management:', courseId);
      return true;
    }
    
    return isAssigned;
  };

  return {
    assignments,
    loading,
    error,
    refetch: fetchAssignments,
    getAssignedCoursesByType,
    isUserAssignedToCourse
  };
};
